import React, { useState } from 'react';
import { Calendar, Users, Mail, Phone } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const BookingCTA = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: '',
    email: '',
    checkin: '',
    checkout: '',
    guests: '2',
    message: ''
  });

  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast({ title: 'Compila i campi obbligatori', description: 'Nome ed email sono necessari per inviare la richiesta.' });
      return;
    }
    toast({ title: 'Richiesta inviata!', description: `Grazie ${form.name}, ti risponderemo al più presto.` });
    setForm({ name: '', email: '', checkin: '', checkout: '', guests: '2', message: '' });
  };

  return (
    <section id="contact" className="bg-[#faf7f2] py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-2 gap-14 lg:gap-24">
        <div>
          <p className="text-[#C87355] text-xs tracking-[0.4em] uppercase mb-5">Contatti</p>
          <h2 className="font-serif text-[#1f1f1f] text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-8">
            Vieni a<br />
            <span className="italic text-[#3A4A2C]">respirare</span> l’Umbria.
          </h2>
          <p className="text-[#555] text-base md:text-lg leading-relaxed mb-10 max-w-md">
            Scrivici per verificare le disponibilità, chiedere informazioni o organizzare un soggiorno su misura tra piscina, artigianato ed eccellenze gastronomiche.
          </p>

          <div className="space-y-5">
            <a href="mailto:info@privateproperties.it" className="flex items-center gap-4 group">
              <div className="w-11 h-11 rounded-full border border-[#3A4A2C]/30 flex items-center justify-center group-hover:bg-[#3A4A2C] group-hover:border-[#3A4A2C] transition-colors">
                <Mail size={16} className="text-[#3A4A2C] group-hover:text-[#faf7f2] transition-colors" />
              </div>
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#888]">Email</div>
                <div className="text-[#1f1f1f] group-hover:text-[#3A4A2C] transition-colors">info@privateproperties.it</div>
              </div>
            </a>
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 rounded-full border border-[#3A4A2C]/30 flex items-center justify-center">
                <Phone size={16} className="text-[#3A4A2C]" />
              </div>
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#888]">Telefono</div>
                <a href="tel:+393452211974" className="text-[#1f1f1f] hover:text-[#3A4A2C] transition-colors">+39 345 22 11 974</a>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="bg-white border border-[#e8e0cf] p-8 md:p-10 shadow-[0_20px_60px_-30px_rgba(58,74,44,0.35)]">
          <h3 className="font-serif text-2xl text-[#1f1f1f] mb-8">Richiedi disponibilità</h3>

          <div className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Nome" name="name" value={form.name} onChange={handle} />
              <Field label="Email" name="email" type="email" value={form.email} onChange={handle} />
            </div>
            <div className="grid grid-cols-2 gap-5">
              <Field label="Check-in" name="checkin" type="date" value={form.checkin} onChange={handle} icon={Calendar} />
              <Field label="Check-out" name="checkout" type="date" value={form.checkout} onChange={handle} icon={Calendar} />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.3em] uppercase text-[#888] mb-2">Ospiti</label>
              <div className="relative">
                <Users size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#3A4A2C]/70" />
                <select
                  name="guests"
                  value={form.guests}
                  onChange={handle}
                  className="w-full pl-10 pr-4 py-3 border border-[#e8e0cf] bg-white text-[#1f1f1f] focus:outline-none focus:border-[#3A4A2C] transition-colors appearance-none"
                >
                  {[1,2,3,4,5,6,7,8].map(n => <option key={n} value={n}>{n} {n === 1 ? 'ospite' : 'ospiti'}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-[10px] tracking-[0.3em] uppercase text-[#888] mb-2">Messaggio</label>
              <textarea
                name="message"
                value={form.message}
                onChange={handle}
                rows="4"
                placeholder="Raccontaci del tuo soggiorno ideale..."
                className="w-full px-4 py-3 border border-[#e8e0cf] bg-white text-[#1f1f1f] focus:outline-none focus:border-[#3A4A2C] transition-colors resize-none"
              />
            </div>

            <button
              type="submit"
              className="w-full py-4 bg-[#3A4A2C] text-[#faf7f2] text-xs tracking-[0.3em] uppercase hover:bg-[#C87355] transition-colors duration-300"
            >
              Invia richiesta
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

const Field = ({ label, name, type = 'text', value, onChange, icon: Icon }) => (
  <div>
    <label className="block text-[10px] tracking-[0.3em] uppercase text-[#888] mb-2">{label}</label>
    <div className="relative">
      {Icon && <Icon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#3A4A2C]/70" />}
      <input
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        className={`w-full ${Icon ? 'pl-10' : 'pl-4'} pr-4 py-3 border border-[#e8e0cf] bg-white text-[#1f1f1f] focus:outline-none focus:border-[#3A4A2C] transition-colors`}
      />
    </div>
  </div>
);

export default BookingCTA;
